/**
 * Bars that sit beside a number — audit v6 V6-01, V6-05, V6-08; `docs/adr/9013`.
 *
 * Three small HTML primitives, no charting library, no new dependency:
 *
 *   `ProportionBar`     4px, a value over its whole, under a KPI ("quanto di cosa")
 *   `CellBar`           the bar inside a numeric table cell (`NumericCell`'s `bar`), with an
 *                       optional reference drawn beneath it — units against requested units
 *   `DivergingBarCell`  a signed value growing left or right of a centre zero
 *
 * **The bar never replaces the number.** Every one of these is drawn beside the figure it
 * encodes, and the figure is the accessible text; the bar is `aria-hidden` decoration except
 * for `ProportionBar`, which stands alone under a KPI and so names its own share.
 *
 * **Colour is a token and a meaning, never a default.** Ink for "how much"; `status-critical`
 * only where the caller says the quantity needs action (broken runs, rejections, losses —
 * audit v6 rule 5: "il rosso solo per ciò che richiede azione"); the delta pair only where a
 * polarity says which side is good, exactly as `DeltaValue` reads it (design/09 §5.3).
 */

import { useTranslation } from 'react-i18next';

import { formatCompactNumber, formatPercent } from '../lib/format';
import type { Polarity } from '../export/types';
import { GHOST_MARK, useGhost } from './ghost';
import { AbsentWords } from './absent-words';
import type { AbsentMeaning } from './figures';
import type { ICUValues } from './display';

/** What a filled bar means. `action` is the red that asks the planner to do something. */
export type BarTone = 'ink' | 'action' | 'positive';

const TONE_FILL: Readonly<Record<BarTone, string>> = {
  ink: 'bg-ink-primary',
  action: 'bg-status-critical',
  positive: 'bg-delta-positive',
};

/** A share in [0, 1] for DRAWING. The text states the true share, which may exceed 1. */
const clampShare = (share: number): number => Math.min(1, Math.max(0, share));

// ── ProportionBar ────────────────────────────────────────────────────────────

export interface ProportionBarProps {
  /** The part. `null` is an absence — an empty track, never a 0% bar. */
  readonly value: number | null;
  /** The whole. `null` or `≤ 0` has no proportion to draw, and the track stays empty. */
  readonly whole: number | null;
  readonly tone?: BarTone;
  /**
   * The text alternative. Receives `{share}` (the formatted percentage) plus `labelValues`.
   * Defaults to "{share} of the whole" — a screen that can name the whole ("of 7,976
   * options") should, because that is the part of the sentence a planner needs.
   */
  readonly labelKey?: string;
  readonly labelValues?: ICUValues;
}

/**
 * A value over its whole, 4px high, under a figure (spec v6b §1 block 2). Not a progress bar
 * and not a gauge: it answers "how much of what", which a bare `2,230` cannot.
 */
export function ProportionBar({
  value, whole, tone = 'ink', labelKey = 'proportion.share', labelValues,
}: ProportionBarProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ghost = useGhost();
  const drawable = !ghost && value !== null && whole !== null && whole > 0;
  const share = drawable ? value / whole : null;
  const label = share === null
    ? t(ghost ? 'ghost.value' : 'proportion.none')
    : t(labelKey, { ...(labelValues ?? {}), share: formatPercent(share, i18n.language) });
  return (
    <div
      role="img"
      aria-label={label}
      data-proportion={share === null ? 'none' : String(share)}
      className="bg-ghost h-1 w-full overflow-hidden rounded-chip"
    >
      {share !== null && (
        <div
          className={`${TONE_FILL[tone]} h-full`}
          style={{ inlineSize: `${clampShare(share) * 100}%` }}
        />
      )}
    </div>
  );
}

// ── the bar inside a numeric cell ────────────────────────────────────────────

export interface CellBarSpec {
  /**
   * The value the full bar length stands for — the column's largest, computed by the caller
   * over EVERY row of the perimeter, not over the fifty drawn (ADR 9008): otherwise a bar's
   * length would change when the planner sorts.
   */
  readonly max: number;
  /**
   * The double variant: a reference the value is read against — `requested_units` under
   * `units`. Drawn beneath the value, in the neutral ramp; `null` is "no reference published".
   */
  readonly reference?: number | null;
  readonly tone?: 'ink' | 'action';
}

/** The drawn part of a `BarCell`. Decorative — the number beside it is the text. */
export function CellBar({ value, bar }: {
  readonly value: number | null;
  readonly bar: CellBarSpec;
}): React.JSX.Element {
  const ghost = useGhost();
  const length = (v: number | null | undefined): number | null => (
    ghost || v === null || v === undefined || !(bar.max > 0) ? null : clampShare(v / bar.max)
  );
  const main = length(value);
  const reference = length(bar.reference);
  return (
    <span aria-hidden data-cell-bar="" className="bg-ghost relative block h-2 min-w-16 flex-1 rounded-chip">
      {reference !== null && (
        <span
          data-cell-bar-reference=""
          className="bg-ramp-neutral-mid absolute inset-y-0 start-0 rounded-chip"
          style={{ inlineSize: `${reference * 100}%` }}
        />
      )}
      {main !== null && (
        <span
          className={`${TONE_FILL[bar.tone ?? 'ink']} absolute inset-y-0 start-0 rounded-chip`}
          style={{ inlineSize: `${main * 100}%` }}
        />
      )}
    </span>
  );
}

// ── DivergingBarCell ─────────────────────────────────────────────────────────

export interface DivergingBarCellProps {
  /** Signed. `null` is an absence — see `absent`. */
  readonly value: number | null;
  /** The magnitude a full half-bar stands for — the column's largest |value|, over every row. */
  readonly maxAbs: number;
  /**
   * Which side is good. REQUIRED, like `DeltaValue`'s: a net benefit below zero is a loss and
   * earns the red; a markdown depth below zero does not. `none` and `target-relative` draw
   * both arms neutral — there is no verdict to colour.
   */
  readonly polarity: Polarity;
  /** REQUIRED — what `null` means in this column (ADR 0012). */
  readonly absent: AbsentMeaning;
  /** Compact above 10,000 with the exact figure in `title`. Default on: this is a scanning column. */
  readonly compact?: boolean;
}

type Verdict = 'favourable' | 'unfavourable' | 'neutral';

function verdictOf(value: number, polarity: Polarity): Verdict {
  if (value === 0 || polarity === 'none' || polarity === 'target-relative') return 'neutral';
  const better = polarity === 'higher-is-better' ? value > 0 : value < 0;
  return better ? 'favourable' : 'unfavourable';
}

const VERDICT_FILL: Readonly<Record<Verdict, string>> = {
  favourable: 'bg-delta-positive',
  unfavourable: 'bg-delta-negative',
  neutral: 'bg-ink-primary',
};

const VERDICT_TEXT: Readonly<Record<Verdict, string>> = {
  favourable: 'text-delta-positive',
  unfavourable: 'text-delta-negative',
  neutral: 'text-ink-primary',
};

/**
 * A signed value around a centre zero, in a table cell (audit v6 V6-05). The same reading as
 * the `diverging-bar` chart form, one row at a time.
 *
 * Never colour alone (§5.2 rule 4): the figure beside the bar always carries its sign — `+`
 * as well as `−` — so the direction survives greyscale and a red-green deficiency. A neutral
 * polarity's negative arm is `ramp-neutral-mid`, the redesign's colour for "the negative arm of
 * a bar" that is neither good nor bad.
 */
export function DivergingBarCell({
  value, maxAbs, polarity, absent, compact = true,
}: DivergingBarCellProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ghost = useGhost();
  const present = !ghost && value !== null;
  const share = present && maxAbs > 0 ? clampShare(Math.abs(value) / maxAbs) : 0;
  const verdict: Verdict = present ? verdictOf(value, polarity) : 'neutral';
  const fill = verdict === 'neutral' && present && value < 0 ? 'bg-ramp-neutral-mid' : VERDICT_FILL[verdict];
  const figure = present
    ? formatCompactNumber(value, i18n.language, compact ? { signed: true } : { signed: true, threshold: Infinity })
    : null;
  return (
    <td className="font-numeric text-numeric tabular-nums">
      <span className="flex items-center gap-2">
        <span aria-hidden data-diverging-bar="" className="flex min-w-16 flex-1 items-center">
          {/* The negative half grows towards the centre, so its bar is end-aligned in it. */}
          <span className="bg-ghost flex h-2 flex-1 justify-end rounded-chip">
            {present && value < 0 && (
              <span data-arm="negative" className={`${fill} h-full rounded-chip`} style={{ inlineSize: `${share * 100}%` }} />
            )}
          </span>
          <span className="border-ink-primary h-3 shrink-0 border-s" />
          <span className="bg-ghost flex h-2 flex-1 rounded-chip">
            {present && value > 0 && (
              <span data-arm="positive" className={`${fill} h-full rounded-chip`} style={{ inlineSize: `${share * 100}%` }} />
            )}
          </span>
        </span>
        {figure === null ? (
          <span className="text-ink-secondary min-w-12 text-end">
            {ghost ? <span aria-label={t('ghost.value')}>{GHOST_MARK}</span> : <AbsentWords absent={absent} />}
          </span>
        ) : (
          <span
            className={`min-w-12 text-end ${VERDICT_TEXT[verdict]}`}
            {...(figure.compacted ? { title: figure.exact } : {})}
          >
            {figure.text}
          </span>
        )}
      </span>
    </td>
  );
}
